type VideoOptions = {
    loop: boolean
    muted: boolean
    /**
     * seconds to skip on start of this video
     */
    startOffset: number
    className?: string
}

const defaultVideoOptions: VideoOptions = {
    loop: false,
    muted: false,
    startOffset: 0,
    className: undefined,
}

export default class Video {
    id: string
    videoElement = document.createElement('video')
    options: VideoOptions

    constructor(src: string, options?: Partial<VideoOptions>) {
        this.id = src
        this.hide()
        this.videoElement.src = src
        this.options = {
            ...defaultVideoOptions,
            ...options,
        }
        this.videoElement.loop = options?.loop ?? false
        this.videoElement.muted = options?.muted ?? false

        if (options?.className) {
            this.videoElement.classList.add(options.className)
        }
    }

    destroy = () => {
        this.videoElement.remove()
    }

    hide = () => {
        this.videoElement.classList.add('hidden')
    }

    show = () => {
        this.videoElement.classList.remove('hidden')
    }

    play = async () => {
        await new Promise<void>((resolve) => {
            this.videoElement.currentTime =
                this.videoElement.currentTime === 0 ? this.options.startOffset ?? 0 : this.videoElement.currentTime
            this.show()

            // WHY: do not overwrite previous onended callback
            const callback = () => {
                resolve()
                this.videoElement.onended = null
            }

            this.videoElement.onended = this.videoElement.onended ?? callback

            this.videoElement.play()
        })
        this.hide()
    }

    pause = () => {
        this.videoElement.pause()
    }

    skip = () => {
        this.videoElement.pause()
        this.videoElement.currentTime = 0
        // @ts-ignore
        this.videoElement.onended && this.videoElement.onended()
        this.videoElement.onended = null
    }

    setVolume = (value: number) => {
        this.videoElement.volume = value
    }

    setMuted = (value: boolean) => {
        this.videoElement.muted = value
    }

    addClickHandler = (handler: () => void) => {
        this.videoElement.onclick = handler
    }

    load = () =>
        new Promise<void>((resolve) => {
            if (this.videoElement.readyState === this.videoElement.HAVE_FUTURE_DATA) {
                resolve()
            } else {
                // race condition 5s
                const timeOut = setTimeout(() => {
                    console.debug(`Inamove::Video => took to long to load: ${this.videoElement.src}`)
                    resolve()
                }, 5000)

                this.videoElement.addEventListener(
                    'canplaythrough',
                    () => {
                        clearTimeout(timeOut)
                        console.debug(`Inamove::Video => can play through ${this.videoElement.src}`)
                        resolve()
                    },
                    { once: true }
                )
            }
        })
}
